import {
  Button, Typography, Chip, Box, IconButton, Collapse,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckIcon from "@mui/icons-material/Check";
import { useUserReminders } from "../hooks/useUserReminders";
import { useMarkReminderCompletedMutation } from "../store/services/remindersv2";
import { useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate } from "react-router-dom";
dayjs.extend(relativeTime);

const typeColor = (type) =>
  type === "URGENT" ? "#ef4444" : type === "WARNING" ? "#f59e0b" : "#3b82f6";

const typeBg = (type) =>
  type === "URGENT" ? "#fef2f2" : type === "WARNING" ? "#fffbeb" : "#eff6ff";

const typeBorder = (type) =>
  type === "URGENT" ? "#fecaca" : type === "WARNING" ? "#fde68a" : "#bfdbfe";

const ReminderPopups = ({ socketReminders = [], onClose }) => {
  const { reminders = [], isLoading, handleRead, isMarking } = useUserReminders();
  const [markCompleted] = useMarkReminderCompletedMutation();
  const navigate = useNavigate();

  const [visible, setVisible] = useState(true);

  const seen = new Set();
  const allReminders = [...socketReminders].filter((r) => {
    if (seen.has(r._id)) return false;
    seen.add(r._id);
    return true;
  });

  const handleDone = async (reminder) => {
    await markCompleted(reminder._id).unwrap();
    onClose?.(reminder._id);
  };

  const handleOpenLead = async (reminder) => {
    // await markCompleted(reminder._id).unwrap();
    onClose?.(reminder._id);

    let leadId = typeof reminder.leadId == "object" ? reminder.leadId._id : reminder.leadId
    if (leadId) {
      // window.open(`/leads/${reminder.leadId._id}`,"_blank");
      navigate(`/leads/${leadId}`)
    }
  };

  if (isLoading || !allReminders.length) return null;

  return (
    <>
      {/* ── reminder stack ───────────────────────────────────── */}
      <Box
        sx={{
          position: "fixed",
          bottom: 80,
          right: 16,
          zIndex: 1400,
          width: 340,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxHeight: "calc(100vh - 120px)",
          overflowY: "auto",
          overflowX: "hidden",
          pr: 0.5,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": { borderRadius: 4, background: "rgba(0,0,0,0.15)" },
        }}
      >
        <Collapse in={visible} unmountOnExit>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {allReminders.map((reminder) => {

              const leadId =
                typeof reminder.leadId === "object"
                  ? reminder.leadId?._id
                  : reminder.leadId;

              return (
                <Box
                  key={reminder._id}
                  sx={{
                    background: reminder.wasMissed ? "#fafafa" : typeBg(reminder.type),
                    border: `1px solid ${reminder.wasMissed ? "#e5e7eb" : typeBorder(reminder.type)}`,
                    borderLeft: `3px solid ${reminder.wasMissed ? "#ef4444" : typeColor(reminder.type)}`,
                    borderRadius: "10px",
                    p: "10px 12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  }}
                >
                  {reminder.wasMissed && (
                    <Typography
                      variant="caption"
                      sx={{ color: "#ef4444", display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
                    >
                      <span style={{
                        width: 6, height: 6, borderRadius: "50%", background: "#ef4444", display: "inline-block",
                      }} />
                      Missed · {dayjs(reminder.reminderTime).fromNow()}
                    </Typography>
                  )}

                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span style={{
                        width: 8, height: 8, borderRadius: "50%", flexShrink: 0, marginTop: 2,
                        background: reminder.wasMissed ? "#9ca3af" : typeColor(reminder.type),
                      }} />
                      <Typography sx={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>
                        {reminder.title}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, ml: 1, flexShrink: 0 }}>
                      <Chip
                        label={reminder.wasMissed ? "Missed" : reminder.type}
                        size="small"
                        sx={{
                          fontSize: 10, height: 18,
                          background: reminder.wasMissed ? "#f3f4f6" : typeBg(reminder.type),
                          color: reminder.wasMissed ? "#6b7280" : typeColor(reminder.type),
                          border: `1px solid ${reminder.wasMissed ? "#e5e7eb" : typeBorder(reminder.type)}`,
                        }}
                      />

                    </Box>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontSize: 12, lineHeight: 1.5, ml: "16px", mb: 1 }}
                  >
                    {reminder.message}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, ml: "16px" }}>
                    {leadId && (
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<OpenInNewIcon sx={{ fontSize: "12px !important" }} />}
                        onClick={() => handleOpenLead(reminder)}
                        sx={{
                          fontSize: 11, py: 0.4, px: 1.5, borderRadius: "6px",
                          textTransform: "none", fontWeight: 500,
                          cursor: "pointer !important",
                          borderColor: reminder.wasMissed ? "#d1d5db" : typeColor(reminder.type),
                          color: reminder.wasMissed ? "#374151" : typeColor(reminder.type),
                          "&:hover": { borderColor: reminder.wasMissed ? "#9ca3af" : typeColor(reminder.type) },
                        }}
                      >
                        Open lead
                      </Button>
                    )}
                    {/* <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={<CheckIcon sx={{ fontSize: "12px !important" }} />}
                    onClick={() => handleDone(reminder)}
                    disabled={isMarking}
                    sx={{
                      fontSize: 11, py: 0.4, px: 1.5, borderRadius: "6px",
                      textTransform: "none", fontWeight: 500,
                      background: reminder.wasMissed ? "#6b7280" : typeColor(reminder.type),
                      cursor: "pointer !important",
                      "&:hover": { opacity: 0.9 },
                    }}
                  >
                    Mark Completed
                  </Button> */}
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Collapse>
      </Box>

      {/* ── slide-in toggle button ────────────────────────────── */}
      <Box
        sx={{
          position: "fixed",
          bottom: 32,
          right: 0,                    // anchored to right edge
          zIndex: 1400,
          transform: visible ? "translateX(0)" : "translateX(calc(100% - 32px))", // mostly hidden, 14px peeking out
          transition: "transform 0.25s ease",
          "&:hover": {
            transform: "translateX(0)", // fully slides in on hover
          },
        }}
      >
        <Button
          variant="contained"
          disableElevation
          startIcon={visible
            ? <NotificationsOffIcon sx={{ fontSize: "15px !important" }} />
            : <NotificationsIcon sx={{ fontSize: "15px !important" }} />
          }
          onClick={() => setVisible((v) => !v)}
          sx={{
            fontSize: 12,
            py: 0.85,
            pl: 2,
            pr: 2.5,
            borderRadius: "20px 0 0 20px",  // left side rounded, right side flush to edge
            textTransform: "none",
            fontWeight: 500,
            whiteSpace: "nowrap",
            background: visible ? "#111827" : "#111827",
            "&:hover": { background: "#1f2937" },
            boxShadow: "-2px 2px 10px rgba(0,0,0,0.18)",
            // red dot for pending count when hidden
            ...((!visible && allReminders.length > 0) && {
              "&::before": {
                content: `"${allReminders.length > 9 ? "9+" : allReminders.length}"`,
                position: "absolute",
                top: -6,
                left: 10,
                background: "#ef4444",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                width: 18,
                height: 18,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: "18px",
                textAlign: "center",
              },
            }),
          }}
        >
          {visible ? "Hide reminders" : "Show reminders"}
        </Button>
      </Box>
    </>
  );
};

export default ReminderPopups;